import { useEffect, useRef } from 'react';

/**
 * useWakeLock — Keeps the screen awake during active sessions.
 * Acquires the wake lock when `active` is true, releases on unmount or when `active` becomes false.
 * Automatically re-acquires on `visibilitychange` (e.g., user switches tabs and comes back).
 */
export function useWakeLock(active: boolean) {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active) {
      // Release if we had one
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      return;
    }

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('[WakeLock] Acquired');
        }
      } catch (err) {
        console.warn('[WakeLock] Failed to acquire:', err);
      }
    };

    requestWakeLock();

    // Re-acquire when page becomes visible again (e.g., returning from another tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && active) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
        console.log('[WakeLock] Released');
      }
    };
  }, [active]);
}
